import {useEffect, useState} from "react";

/**
 * Tracks if a container's width exceeds a certain threshold.
 * Useful for bridging CSS `@container` queries with JavaScript logic (like conditional navigation).
 *
 * @param ref - The ref of the container element to track.
 * @param threshold - The width threshold in pixels (default: 672px, equivalent to Tailwind's `2xl` container or `max-w-2xl`).
 * @returns boolean indicating if the container's width is greater than or equal to the threshold.
 */
export function useContainerBreakpoint(
  ref: React.RefObject<HTMLElement | null>,
  threshold: number = 672,
) {
  const [isPastThreshold, setIsPastThreshold] = useState(false);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    // Find the nearest container to accurately match CSS container queries,
    // avoiding inner paddings that would reduce the measured width.
    const target = element.closest('[class~="@container"]') || element;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Container queries match against the element's layout width.
        const width = entry.target.getBoundingClientRect().width;

        setIsPastThreshold(width >= threshold);
      }
    });

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [ref, threshold]);

  return isPastThreshold;
}
